import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    OnInit,
    signal,
    ViewChild
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import {FormArray, FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {ReferenceService} from '../../../../modules/apiModule/api/reference.service';
import {map} from 'rxjs/operators';
import {MatCheckbox} from '@angular/material/checkbox';
import {AccountControllerService} from '../../../../modules/apiModule/api/account-controller.service';
import {UserUpdateDto} from '../../../../modules/apiModule/model/user-update-dto';
import {UserService} from '../../../../core/services/user.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MultiSelectComponent} from '../../multi-select/multi-select.component';
import {FacilityService} from '../../../../modules/apiModule/api/facility.service';
import {RemarktypeService} from '../../../../modules/apiModule/api/remarktype.service';
import {ApiRemarktypeGetRequestParams} from '../../../../modules/apiModule';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {CommunicationService} from '../../../../core/services/communication.service';
import {RemarkType} from '../../../../modules/apiModule/model/remarktype';
import {FirmService} from '../../../../modules/apiModule/api/firm.service';
import {first, switchMap} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../../environments/environment';
import {EventBusService} from '../../../../core/services/event-bus.service';

@Component({
    selector: 'ostso-account',
    standalone: true,
    imports: [
        MatIcon,
        ReactiveFormsModule,
        MatCheckbox,
        MatProgressSpinner,
        MultiSelectComponent
    ],
    templateUrl: './account.component.html',
    styleUrl: './account.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountComponent implements OnInit {

    data: User = inject(MAT_DIALOG_DATA);
    dialogRef = inject(MatDialogRef<AccountComponent>);
    fb = inject(FormBuilder);
    referenceService = inject(ReferenceService);
    accountService = inject(AccountControllerService);
    facilityService = inject(FacilityService);
    remarktypeService = inject(RemarktypeService);
    organizationService = inject(FirmService);
    userService = inject(UserService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly toast = inject(CommunicationService);
    private readonly http = inject(HttpClient);
    private readonly eventBusService = inject(EventBusService);


    @ViewChild('select') select?: ElementRef;

    get isAdmin(): boolean {
        return this.data.userRoles.some(e => e.code === 'admin');
    }

    get isInspector(): boolean {
        return this.data.userRoles.some(e => e.code === 'inspector');
    }

    get isFacility(): boolean {
        return this.data.userRoles.some(e => e.code === 'facility');
    }

    get isOperator(): boolean {
        return this.data.userRoles.some(e => e.code === 'operator');
    }

    rateStatusIntervals: RateStatusInterval[] = [];

    form = this.fb.group({
        statuses: this.fb.array([], Validators.required),
    });

    formFacilitiesRemarktype = this.fb.group({
        facilities: new FormControl<Facility[]>([]),
        remarkTypes: new FormControl<RemarkType[]>([]),
    });
    facilitiesSignal = toSignal(this.formFacilitiesRemarktype.get('facilities')!.valueChanges);
    dataFacilitiesBySelected = computed(() => this.dataFacility().filter(e => this.facilitiesSignal()?.length ? !this.facilitiesSignal()!.some(f => f.id === e.id) : true));

    remarktypesSignal = toSignal(this.formFacilitiesRemarktype.get('remarkTypes')!.valueChanges);
    dataRemarktypesBySelected = computed(() => this.dataRemarktypes().filter(e => this.remarktypesSignal()?.length ? !this.remarktypesSignal()!.some(f => f.id === e.id) : true));

    get statuses(): FormArray {
        return this.form.get('statuses')! as FormArray;
    }

    get statusesValue(): boolean[] {
        return this.statuses.controls.map(control => !!control.value);
    }

    accountData = {
        ['Роль']: this.data.userRoles[0].name,
        ['ФИО']: `${this.data.lastName} ${this.data.firstName} ${this.data.middleName}`,
        ['Должность']: this.data.position,
        ['Электронная почта']: this.data.emailAddress,
        ['Телефон']: this.data.phone,
    };

    dataFacility = signal<Facility[]>([]);
    dataRemarktypes = signal<RemarkType[]>([]);

    ngOnInit(): void {
        this.statuses.valueChanges.subscribe(status => {
            console.log(this.statusesValue);
        });
        this.referenceService.apiReferenceRateStatusIntervalsGet()
            .pipe(map(v => v.values))
            .subscribe((v: RateStatusInterval[]) => {
                console.log('apiReferenceRateStatusIntervalsGet', v);
                v.forEach((item: RateStatusInterval) => {
                    this.rateStatusIntervals.push(item);
                    this.statuses.push(this.fb.control(item.selectedByDefault, Validators.required));
                });
                this.data.rateStatusIntervalList.forEach((item: RateStatusInterval) => {
                    const found = this.rateStatusIntervals.findIndex(v => v.id === item.id);
                    if (found !== -1) this.statuses.controls[found].setValue(true);
                });
                this.cdr.detectChanges();
            });
        const defaultParams: ApiRemarktypeGetRequestParams = {
            pageSize: 1e5,
            statusId: 1
        };

        this.formFacilitiesRemarktype.get('facilities')!.setValue(this.data.facilities ?? []);
        this.formFacilitiesRemarktype.get('remarkTypes')!.setValue((this.data.remarkTypes as RemarkType[] | undefined) ?? []);

        //--- ищем организацию ---
        const handlerObject = {
            next: (v: any) => {
                this.dataFacility.set(v.values);
            },
            error: () => {
                this.toast.error('Ошибка при получении списка объектов');
            }
        };

        if (this.data.firmName) {
            this.organizationService.apiFirmGet({...defaultParams, searchString: this.data.firmName})
                .pipe(
                    switchMap(v => this.facilityService.apiRemarktypeGet({
                        ...defaultParams,
                        FirmIds: v.values[0].id ? [v.values[0].id] : undefined
                    })),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(handlerObject);
        } else {
            this.facilityService.apiRemarktypeGet({
                ...defaultParams
            }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(handlerObject);
        }


        //--- СПИСОК ЗАМЕЧАНИЙ  ---
        this.remarktypeService.apiRemarktypeGet({
            ...defaultParams,
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: v => {
                    this.dataRemarktypes.set(v.values);
                },
                error: () => {
                    this.toast.error('Ошибка при получении списка замечаний');
                }
            });
    }

    reset(): void {
        this.statuses.setValue([true, false, false, false]);
    }

    async apply(): Promise<void> {
        if (this.isInspector) {
            console.log(this.statuses.value);
            const rateStatusIntervalList = this.statuses.value.map((v: boolean, i: number) => v && this.rateStatusIntervals[i]).filter(Boolean);
            this.accountService.apiAccountIdPut({
                id: this.data.id,
                apiAccountIdPutRequest: {...this.data, rateStatusIntervalList} as UserUpdateDto
            })
                .subscribe(v => {
                    console.log(v);
                    this.userService.user$.next(v);
                    this.dialogRef.close();
                });
        }
        if (this.isFacility) {
            const baseURL = environment.api_url;
            const URL = `${baseURL}/api/user/${this.data.id}/refs`;

            const newData = {
                ...this.data,
                facilities: this.facilitiesSignal(),
                remarkTypes: this.remarktypesSignal()
            };

            this.http.put<User>(URL, newData)
                .pipe(first(), takeUntilDestroyed(this.destroyRef))
                .subscribe(v => {
                    console.log(v);
                    const {facilities, remarkTypes} = v;
                    const newData = {...this.userService.user$.value, facilities, remarkTypes};
                    this.userService.user$.next(newData);
                    this.dialogRef.close();
                    this.eventBusService.emit('updateRateCard');
                });
        }
    }

    protected readonly Object = Object;

}
